/**
 * BasePawnMovementComponent - abstract base for pawn locomotion. Defines movement
 * and look input interfaces. Subclasses implement physics-specific behavior.
 */
import * as THREE from 'three';

import { NetMovementPredictor } from '../../multiplayer/MovementPrediction.js';
import { AbstractEngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';
import { SceneComponent } from '../SceneComponent.js';

import type { Pawn } from '../../actors/Pawn.js';


/**
 * Input structure that can represent either absolute values or speed/rate values
 */
export class PawnInput {
  /** The input value */
  @property({ replicate: true })
    value: number = 0;

  /** True if the value represents an absolute amount, false if it represents a speed/rate */
  @property({ replicate: true })
    isAbsolute: boolean = false;

  constructor(value: number = 0, isAbsolute: boolean = false) {
    this.value = value;
    this.isAbsolute = isAbsolute;
  }
}

/**
 * Local input state for the pawn movement component
 */
export interface BasePawnMovementInputState {
  forwardInput: number;
  rightInput: number;
  lookUpInput: number;
  lookRightInput: number;
  zoomInput: number;
}

/**
 * Base class for all pawn movement components
 * Provides abstract methods for handling movement input
 */
@AbstractEngineClass('BasePawnMovementComponent')
export abstract class BasePawnMovementComponent extends SceneComponent {
  @property()
  public speedModifier: number = 1;

  @property()
  public jumpStrengthModifier: number = 1;

  @property({ replicate: true, transient: true, hidden: true })
  protected forwardInput: PawnInput = new PawnInput();

  @property({ replicate: true, transient: true, hidden: true })
  protected rightInput: PawnInput = new PawnInput();

  @property({ replicate: true, transient: true, hidden: true })
  protected lookUpInput: PawnInput = new PawnInput();

  @property({ replicate: true, transient: true, hidden: true })
  protected lookRightInput: PawnInput = new PawnInput();

  @property({ replicate: true, transient: true, hidden: true })
  protected zoomInput: PawnInput = new PawnInput();

  public getInputState(): BasePawnMovementInputState {
    return {
      forwardInput: this.forwardInput.value,
      rightInput: this.rightInput.value,
      lookUpInput: this.lookUpInput.value,
      lookRightInput: this.lookRightInput.value,
      zoomInput: this.zoomInput.value,
    };
  }

  public addForwardInput(value: number, isAbsolute: boolean = false): void {
    this.forwardInput = new PawnInput(value, isAbsolute);
  }

  public addRightInput(value: number, isAbsolute: boolean = false): void {
    this.rightInput = new PawnInput(value, isAbsolute);
  }

  public addLookUpInput(value: number, isAbsolute: boolean = false): void {
    this.lookUpInput = new PawnInput(value, isAbsolute);
  }

  public addLookRightInput(value: number, isAbsolute: boolean = false): void {
    this.lookRightInput = new PawnInput(value, isAbsolute);
  }

  public addZoomInput(value: number, isAbsolute: boolean = false): void {
    this.zoomInput = new PawnInput(value, isAbsolute);
  }

  public setPawnWorldTransform(transform: { position?: THREE.Vector3, rotation?: THREE.Euler, scale?: THREE.Vector3 }): void {
    const owner = this.getActor()!;
    const rootComponent = owner.rootComponent;
    if (transform.position) {
      rootComponent.position.copy(transform.position);
    }
    if (transform.rotation) {
      rootComponent.rotation.copy(transform.rotation);
    }
    if (transform.scale) {
      rootComponent.scale.copy(transform.scale);
    }
  }

  public jump(strength: number = 1): void { }

  public stopJump(): void { }

  /**
   * Handles mouse wheel events from the input system.
   * Override in subclasses to implement scroll-based behavior (e.g., zoom, speed adjustment).
   * @param e - The wheel event
   * @returns True if the event was handled, false otherwise
   */
  public handleMouseWheel(e: WheelEvent): boolean {
    return false;
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    // Only track local transforms for actors with local simulation (not simulated proxies)
    const owner = this.getActor() as Pawn | null;
    const actorPredictor = owner?.getMovementPredictor();

    if (owner && actorPredictor && !owner.isSimulatedProxy()) {
      const rootComponent = owner.rootComponent;
      actorPredictor.addLocalTransform({
        timestamp: NetMovementPredictor.currentTime(),
        position: rootComponent.position,
        rotation: rootComponent.quaternion,
        scale: rootComponent.scale,
      });
    }
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    const owner = this.getActor() as Pawn | null;
    const actorPredictor = owner?.getMovementPredictor();

    if (owner && actorPredictor) {
      const rootComponent = owner.rootComponent;
      // Use simple interpolation for simulated proxies (no local simulation)
      // Use prediction for autonomous proxies (with local simulation)
      const useSimpleInterpolation = owner.isSimulatedProxy();
      const predictedTransform = actorPredictor.predictCurrentTransform(useSimpleInterpolation);
      if (predictedTransform) {
        const lerpedTransform = NetMovementPredictor.lerpTransform({
          position: rootComponent.position,
          rotation: rootComponent.quaternion,
          scale: rootComponent.scale,
        }, predictedTransform, NetMovementPredictor.LerpAlpha);
        this.setPawnWorldTransform({
          position: lerpedTransform.position,
          rotation: new THREE.Euler().setFromQuaternion(lerpedTransform.rotation),
          scale: lerpedTransform.scale,
        });
      }
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Path';
  }
}
